<template>
    <v-row>
        <v-col cols="12" class="title-section pb-0">
            {{ $t('common.panel_daily') }}
        </v-col>
        <v-col cols="12">
            <div class="table-container">
                <v-data-table
                    :headers="headers"
                    :loading="loading"
                    :items="students"
                    :items-per-page="itemsPerPage"
                    @page-count="pageCount = $event"
                    :page.sync="page"
                    :mobile-breakpoint="0"
                    hide-default-footer
                    :search="search"
                    sort-by="status"
                    :disable-sort="!!studentId"
                    :no-data-text="$t('common.not_found')"
                    :loading-text="$t('common.loading')"
                >
                    <template v-slot:item.img="{ item }">
                        <v-avatar color="accent" size="45" class="overflow-visible">
                            <v-img :src="item.img" :contain="item.img === imgPlaceholder"/>
                            <v-avatar v-if="item.hasInjury" class="injury-icon" size="20">
                                <v-icon color="error">$injury</v-icon>
                            </v-avatar>
                        </v-avatar>
                    </template>
                    <template v-slot:item.lastWellness="{ item }">
                        <div class="text-center">
                            <v-avatar v-for="(wellness,i) in item.lastWellness" :key="i" :color="wellness" size="10"
                                      class="mr-1"/>
                        </div>
                    </template>
                    <template v-slot:item.actions="{ item }">
                        <v-tooltip left :color="item.hasNote ? '#333333' : 'transparent'">
                            <template v-slot:activator="{ on, attrs }">
                                <v-btn class="mr-1 mb-1"
                                       fab x-small depressed
                                       v-bind="attrs" v-on="on"
                                       @click="showNoteCoachDialog(item)"
                                       :color="item.hasNote ? 'secondary' : null"
                                >
                                    <v-icon>{{ item.hasNote ? 'mdi-note-edit' : 'mdi-note-plus' }}</v-icon>
                                </v-btn>
                            </template>
                            <span v-html="formatMessage(item)"/>
                        </v-tooltip>
                        <v-btn class="mr-1 mb-1" fab x-small depressed @click="toStudentProfile(item.id)">
                            <v-icon>mdi-magnify</v-icon>
                        </v-btn>
                    </template>
                    <template v-slot:footer v-if="pageCount > 0 && !studentId">
                        <kimsa-pagination
                            @page="page = $event"
                            :pageCount="pageCount"
                        />
                    </template>
                </v-data-table>
                <notes-student-dialog ref="notes" @saved="onSaveNotes"/>
            </div>
        </v-col>
    </v-row>
</template>

<script>
import {Client, UserIndex} from "@/utils/Algolia";
import KimsaPagination from "@/components/common/KimsaPagination";
import NotesStudentDialog from "@/components/coach/NotesStudentDialog";

export default {
    name: "StudentProgressTable",
    components: {NotesStudentDialog, KimsaPagination},
    props: {
        coachId: {
            type: String,
            default: null,
        },
        studentId: {
            type: String,
            default: null,
        }
    },
    data(vm = this) {
        return {
            students: [],
            loading: false,
            student: null,
            search: '',
            page: 1,
            pageCount: 0,
            itemsPerPage: 10,
        }
    },
    computed: {
        headers(vm = this) {
            let headers = [
                {text: '', value: 'img', sortable: false},
                {text: vm.$t('common.name'), value: 'name'},
                {text: vm.$t('common.week'), value: 'week'},
                {text: vm.$t('common.month'), value: 'month'},
                {text: vm.$t('common.last_wellness'), value: 'lastWellness', width: '130px'},
                {text: '', value: 'actions', sortable: false},
            ]
            if (vm.studentId) return headers.filter(h => h.value !== 'actions')
            return headers
        },
    },
    async mounted() {
        const vm = this
        if (vm.coachId) await vm.loadStudents()
        if (vm.studentId) vm.loadStudentData()
    },
    methods: {
        async loadStudents() {
            const vm = this
            vm.loading = true
            vm.students = []

            let filter = `studentSubscription.coachId: ${vm.coachId}`
            // console.log('search students', filter)

            await Client.clearCache()
            await UserIndex.search('', {
                filters: filter,
                hitsPerPage: 10000
            })
                .then(({hits}) => {
                    vm.students = hits.filter(hit => !(hit.studentSubscription.paused)).map(hit => ({
                        id: hit.objectID,
                        name: hit.name,
                        email: hit.email,
                        img: hit.avatar || vm.imgPlaceholder,
                        week: hit.trainingDays ? `${hit?.counter?.week || 0}/${hit.trainingDays}` : `${hit?.counter?.week || 0}/7`,
                        month: hit?.counter?.month || 0,
                        lastWellness: vm.fillWellness(hit.lastWellness || []),
                        hasNote: hit?.coachNote?.message || false,
                        coachNote: hit?.coachNote || null,
                        hasInjury: hit?.hasInjury || false,
                    }))
                    vm.loading = false
                })
                .catch(err => {
                    console.log(err);
                });
        },
        loadStudentData() {
            const vm = this
            vm.loading = true

            vm.students = [{
                id: vm.studentId,
                name: vm.account.name,
                email: vm.account.email,
                img: vm.account?.avatar || vm.imgPlaceholder,
                week: vm.account?.trainingDays ? `${vm.account?.counter?.week || 0}/${vm.account.trainingDays}` : `${vm.account?.counter?.week || 0}/7`,
                month: vm.account?.counter?.month || 0,
                lastWellness: vm.fillWellness(vm.account?.lastWellness || []),
                hasInjury: vm.account?.hasInjury || false,
            }]

            vm.loading = false
        },
        toStudentProfile(studentId) {
            const vm = this
            vm.$router.push({name: 'coach.students.profile', params: {uid: studentId}})
        },
        fillWellness(wellness) {
            const vm = this
            let array = []

            const lastSixValues = []
            for (let i = 5; i < wellness.length; i++) lastSixValues.push(wellness.slice(i - 5, i + 1))

            for (let i = 0; i < lastSixValues.length; i++) {
                const subArray = lastSixValues[i]
                const avg = Number(subArray.reduce((a, b) => a + b) / subArray.length).toFixed(2)
                const desvest = vm.calcDesvest(subArray)
                const currentValue = wellness[i + 5]
                array.push(Number((currentValue - avg) / desvest).toFixed(2))
            }

            let newArray = new Array(7).fill('#000000')
            newArray.splice(0, array.length, ...array.map(item => vm.getColorWellness(Number(item))));
            return newArray
        },
        calcDesvest(array) {
            const avg = Number(array.reduce((a, b) => a + b) / array.length).toFixed()
            const desvests = array.map(x => Math.pow(x - avg, 2))
            if (desvests.every(element => element === 0)) return 1
            const sumDesvests = desvests.reduce((a, b) => a + b)
            return Math.sqrt(sumDesvests / array.length).toFixed(2)
        },
        getColorWellness(wellness) {
            if (wellness <= -1.65) return '#fd2e00'
            else if (wellness < 1.65) return '#aeaeae'
            else return '#5da219'
        },
        showNoteCoachDialog(student) {
            const vm = this
            vm.$refs.notes.show(student)
        },
        onSaveNotes(studentId, notes) {
            const vm = this
            let index = vm.students.findIndex(student => student.id === studentId)
            if (index >= 0) {
                vm.students[index].hasNote = !!notes
                vm.students[index].coachNote = notes || null
            }
        },
        formatMessage(student) {
            const vm = this
            let message = student.coachNote?.message || ''
            return message.replace(/\n/g, '<br>');
        }
    },
    watch: {
        coachId(val) {
            if (val) this.loadStudents()
        }
    }
}
</script>

<style scoped>

.title-section {
    font-weight: 700;
    font-size: 20px;
    line-height: 22px;
}

.table-container {
    border: thin solid rgba(0, 0, 0, 0.12);
    border-radius: 8px;
    overflow: hidden;
}

.injury-icon {
    position: absolute;
    bottom: -4px;
    right: -4px;
    border: 1px solid var(--v-error-base) !important;
    background-color: rgba(255,255,255,0.8)
}

</style>